/**
 * The agent's `offer_action` card: one or two sentences on what it would do,
 * and the choices as buttons ("Retitle 212 files", "Start with Employment
 * (140)", "Keep current names"). A click is the attorney's next message; the
 * server reads the choice off it, so the agent acts on what was offered.
 *
 * The same card renders an offer added under a finished reply (a
 * `data-offer` part: save what the conversation taught as a skill).
 */
import type { DynamicToolUIPart, ToolUIPart } from "ai";
import { ArrowRight, CheckCircle2, Sparkles } from "lucide-react";
import { useMemo, type FC } from "react";

import { Suggestion, Suggestions } from "@/components/ai-elements/suggestion";
import { cn } from "@/lib/utils";

import { offerFromToolOutput, offerOf } from "../offers/model";
import { useOfferActionsContext } from "../offers/OfferActionsContext";
import {
  SkillOfferFollowThrough,
  SkillOfferPreview
} from "./SkillOfferPreview";

type Props = (
  | { part: ToolUIPart | DynamicToolUIPart; data?: never }
  | { part?: never; data: unknown }
) & { className?: string };

/** Edit in Skills on a `save_skill` offer. */
const EDIT_SKILL = "edit";

export const OfferCard: FC<Props> = ({ part, data, className }) => {
  const offer = useMemo(() => {
    if (!part) return offerOf(data);
    return part.state === "output-available"
      ? offerFromToolOutput(part.output)
      : null;
  }, [part, data]);
  const actions = useOfferActionsContext();
  if (!offer) return null;

  const state = actions?.stateOf(offer.id) ?? null;
  const chosenId = state?.choice ?? offer.choice;
  const chosen = chosenId
    ? offer.choices.find((c) => c.id === chosenId) ?? null
    : null;
  const closed =
    !chosen && (state?.superseded || offer.status === "superseded");
  const canChoose = Boolean(actions) && !chosen && !closed;

  return (
    <div
      className={cn(
        "rounded-lg border border-border bg-background p-3 text-base",
        className
      )}
      data-testid="offer-card"
      data-offer-kind={offer.kind}
      data-offer-state={chosen ? "decided" : closed ? "closed" : "open"}
    >
      <div className="flex items-start gap-1.5">
        <Sparkles className="mt-0.5 size-3.5 shrink-0 text-muted-foreground" />
        <p className="min-w-0 flex-1 text-fg">{offer.summary || offer.label}</p>
      </div>
      {offer.preview?.length ? (
        <ul
          className="mt-2 flex flex-col gap-0.5 rounded border border-line-2 bg-fill-1 px-2 py-1.5 text-sm"
          data-testid="offer-preview"
        >
          {offer.preview.map((row) => (
            <li
              key={`${row.before}->${row.after}`}
              className="flex min-w-0 items-center gap-1.5"
            >
              {row.before ? (
                <>
                  <span
                    className="min-w-0 max-w-[45%] truncate text-muted-foreground"
                    title={row.before}
                  >
                    {row.before}
                  </span>
                  <ArrowRight className="size-3 shrink-0 text-muted-foreground" />
                </>
              ) : null}
              <span
                className="min-w-0 flex-1 truncate text-foreground"
                title={row.after}
              >
                {row.after}
              </span>
            </li>
          ))}
        </ul>
      ) : null}
      {offer.skill ? <SkillOfferPreview skill={offer.skill} /> : null}
      {offer.skill && chosen?.id === EDIT_SKILL && actions ? (
        <SkillOfferFollowThrough
          skillId={offer.skill.skillId}
          busy={actions.busy}
        />
      ) : null}
      {chosen ? (
        <div
          className="mt-2 flex items-center gap-1.5 text-sm text-muted-foreground"
          data-testid="offer-chosen"
        >
          <CheckCircle2
            className={cn(
              "size-3.5",
              chosen.decline ? "text-muted-foreground" : "text-success"
            )}
          />
          {chosen.label}
        </div>
      ) : closed ? (
        <div className="mt-2 text-sm text-muted-foreground">
          Replaced by a newer offer.
        </div>
      ) : canChoose && actions ? (
        <Suggestions className="mt-2.5">
          {offer.choices.map((choice, index) => (
            <Suggestion
              key={choice.id}
              suggestion={choice.label}
              variant={
                choice.decline
                  ? "ghost"
                  : index === 0 && offer.kind !== "next_steps"
                  ? "default"
                  : "outline"
              }
              size="sm"
              className="h-7 px-3 text-sm"
              title={choice.description}
              disabled={actions.busy}
              onClick={() => actions.choose(offer, choice)}
              data-testid={`offer-choice-${choice.id}`}
            />
          ))}
        </Suggestions>
      ) : null}
    </div>
  );
};
